import type { NoteName, Scale } from '@tunasong/schemas'
import { isChordVariant, type Chord } from '../chord/index.js'
import { getNoteByChromaticOffset, getNoteChromaticIndex, getNotesByChromaticIndex } from '../note/note-lib.js'
import ScaleLib from '../scale/scale.js'
import { getDiatonicFunction } from './diatonic.js'

/**
 * Reharm Lab v1 (Phase 2 Tier 2) — analyzer.
 *
 * `getReharmOptions(slotIndex, progression, key) → Substitution[]` returns one
 * entry per technique in a stable locked order. Each entry reports whether the
 * technique applies (`available`) and, if not, why (`applicabilityReason`).
 * The synthesized chord is always populated so the UI can render a
 * before/after chip even on disabled cards.
 *
 * Spec: docs/superpowers/specs/2026-05-19-reharm-lab-design.md §3.3 + §3.4
 * Bowie scope memo: .claude/agent-memory/bowie/project_reharm_lab_v1_scope.md
 */

export type SubstitutionKind = 'replace' | 'insert-before' | 'replace-next'

export type SubstitutionTechnique =
  | 'tritone-sub'
  | 'secondary-dominant'
  | 'borrowed-iv'
  | 'borrowed-flat-VII'
  | 'deceptive-resolution'

export type ApplicabilityReason =
  | 'role-mismatch'
  | 'slot-position'
  | 'next-chord-mismatch'
  | 'non-diatonic'

export interface Substitution {
  technique: SubstitutionTechnique
  kind: SubstitutionKind
  chord: Chord
  lexiconId: string
  available: boolean
  applicabilityReason?: ApplicabilityReason
  romanLabel: string
}

const TECHNIQUE_ORDER: SubstitutionTechnique[] = [
  'tritone-sub',
  'secondary-dominant',
  'borrowed-iv',
  'borrowed-flat-VII',
  'deceptive-resolution',
]

const LEXICON_IDS: Record<SubstitutionTechnique, string> = {
  'tritone-sub': 'subst-tritone-sub',
  'secondary-dominant': 'subst-secondary-dominant',
  'borrowed-iv': 'subst-borrowed-iv',
  'borrowed-flat-VII': 'subst-borrowed-flat-VII',
  'deceptive-resolution': 'subst-deceptive-resolution',
}

const KIND_PER_TECHNIQUE: Record<SubstitutionTechnique, SubstitutionKind> = {
  'tritone-sub': 'replace',
  'secondary-dominant': 'insert-before',
  'borrowed-iv': 'replace',
  'borrowed-flat-VII': 'replace',
  'deceptive-resolution': 'replace-next',
}

/**
 * Lower `note` by one semitone, preserving the diatonic letter where
 * possible (B → Bb, not A#; F → Fb, not E). Falls back to the first
 * standard-set enharmonic when the same-letter spelling doesn't exist
 * (e.g., F# → F, since "F-natural" is its own chromatic position).
 */
const lowerNoteKeepingLetter = (note: NoteName): NoteName => {
  const targetIndex = (getNoteChromaticIndex(note) - 1 + 12) % 12
  const candidates = getNotesByChromaticIndex(targetIndex)
  const sameLetter = candidates.find(n => n.startsWith(note[0]))
  if (sameLetter) {
    return sameLetter
  }
  const fallback = getNoteByChromaticOffset(note, -1)
  return fallback as NoteName
}

/**
 * Bowie-locked spelling for the tritone-sub root by major-key tonic
 * (Reharm Lab v1 §8.2). The substitution root is always ♭II of the target
 * key (the song key, since strict V→I is the only target shape in v1).
 *
 * Where the literal ♭II would be a double-flat (A♭ major's B𝄫, D♭ major's E𝄫),
 * or the awkward single-flat F♭ (E♭ major), v1 respells as the enharmonic
 * natural for readability.
 *
 * Locked verbatim — do not edit without Bowie review.
 */
const TRITONE_SUB_ROOT_BY_KEY: Partial<Record<NoteName, NoteName>> = {
  C: 'Db',
  D: 'Eb',
  E: 'F',
  F: 'Gb',
  'F#': 'G',
  Gb: 'G',
  G: 'Ab',
  A: 'Bb',
  B: 'C',
  Bb: 'Cb',
  Eb: 'E',
  Ab: 'A',
  Db: 'D',
  'C#': 'D',
}

export const getReharmOptions = (
  slotIndex: number,
  progression: readonly Chord[],
  key: Scale
): Substitution[] => TECHNIQUE_ORDER.map(technique => buildSubstitution(technique, slotIndex, progression, key))

const buildSubstitution = (
  technique: SubstitutionTechnique,
  slotIndex: number,
  progression: readonly Chord[],
  key: Scale
): Substitution => {
  switch (technique) {
    case 'tritone-sub':
      return buildTritoneSub(slotIndex, progression, key)
    case 'secondary-dominant':
      return buildSecondaryDominant(slotIndex, progression, key)
    case 'borrowed-iv':
      return buildBorrowedIv(slotIndex, progression, key)
    case 'borrowed-flat-VII':
      return buildBorrowedFlatVII(slotIndex, progression, key)
    case 'deceptive-resolution':
      return buildDeceptiveResolution(slotIndex, progression, key)
  }
}

const unavailable = (
  technique: SubstitutionTechnique,
  slotChord: Chord,
  reason: ApplicabilityReason
): Substitution => ({
  technique,
  kind: KIND_PER_TECHNIQUE[technique],
  chord: slotChord,
  lexiconId: LEXICON_IDS[technique],
  available: false,
  applicabilityReason: reason,
  romanLabel: '',
})

const buildTritoneSub = (
  slotIndex: number,
  progression: readonly Chord[],
  key: Scale
): Substitution => {
  const current = progression[slotIndex]
  const next = progression[slotIndex + 1]

  if (!next) {
    return unavailable('tritone-sub', current, 'next-chord-mismatch')
  }
  if (!isChordVariant(current) || !isChordVariant(next)) {
    return unavailable('tritone-sub', current, 'role-mismatch')
  }

  // Strict v1: current must be V (plain or dom7) AND next must resolve to I.
  // Bowie §8.1 lenient mode: plain V is accepted (the synthesized chord is
  // always dom7 — the original V is V7-implicit). Bowie §8.7: V→non-tonic
  // tritone sub is disabled in v1.
  const currentFunction = getDiatonicFunction(current, key)
  if (currentFunction !== 'V' || (current.variant !== '' && current.variant !== '7')) {
    return unavailable('tritone-sub', current, 'role-mismatch')
  }

  const nextFunction = getDiatonicFunction(next, key)
  if (nextFunction !== 'I') {
    return unavailable('tritone-sub', current, 'next-chord-mismatch')
  }

  // Bowie §8.2 — synthesize ♭II of the *target key* (= song key here, since
  // v1 only ships strict V→I tritone sub). The Bowie-locked spelling table
  // respells double-flats (B𝄫, E𝄫) and the awkward F♭ as their enharmonic
  // naturals for lead-sheet readability.
  const subRoot = TRITONE_SUB_ROOT_BY_KEY[key.root]
  if (!subRoot) {
    return unavailable('tritone-sub', current, 'role-mismatch')
  }
  const synthesized: Chord = { root: subRoot, variant: '7' }

  return {
    technique: 'tritone-sub',
    kind: 'replace',
    chord: synthesized,
    lexiconId: LEXICON_IDS['tritone-sub'],
    available: true,
    romanLabel: '♭II7/I',
  }
}

const buildSecondaryDominant = (
  slotIndex: number,
  progression: readonly Chord[],
  key: Scale
): Substitution => {
  const current = progression[slotIndex]

  if (slotIndex === 0) {
    return unavailable('secondary-dominant', current, 'slot-position')
  }
  if (!isChordVariant(current)) {
    return unavailable('secondary-dominant', current, 'role-mismatch')
  }

  // Bowie §8.3 — strict diatonic-only. Targets must be major or minor diatonic
  // chords of the key (I, ii, iii, IV, V, vi). vii° is excluded — v1 doesn't
  // ship V7/vii°.
  const currentFunction = getDiatonicFunction(current, key)
  if (!currentFunction) {
    return unavailable('secondary-dominant', current, 'non-diatonic')
  }
  if (currentFunction === 'vii°') {
    return unavailable('secondary-dominant', current, 'role-mismatch')
  }

  // V7 of X = dom7 chord rooted a perfect fifth above the target. P5 = +7
  // semitones.
  const dominantRoot = getNoteByChromaticOffset(current.root, 7) as NoteName
  const synthesized: Chord = { root: dominantRoot, variant: '7' }

  return {
    technique: 'secondary-dominant',
    kind: 'insert-before',
    chord: synthesized,
    lexiconId: LEXICON_IDS['secondary-dominant'],
    available: true,
    romanLabel: `V7/${currentFunction}`,
  }
}

const buildBorrowedIv = (
  slotIndex: number,
  progression: readonly Chord[],
  key: Scale
): Substitution => {
  const current = progression[slotIndex]
  if (!isChordVariant(current)) {
    return unavailable('borrowed-iv', current, 'role-mismatch')
  }
  // Bowie §8.6 — major-key only in v1; minor IV (or minor key) does not apply.
  if (getDiatonicFunction(current, key) !== 'IV') {
    return unavailable('borrowed-iv', current, 'role-mismatch')
  }
  // Same root, minor variant.
  const synthesized: Chord = { root: current.root, variant: 'm' }
  return {
    technique: 'borrowed-iv',
    kind: 'replace',
    chord: synthesized,
    lexiconId: LEXICON_IDS['borrowed-iv'],
    available: true,
    romanLabel: 'iv',
  }
}

const buildBorrowedFlatVII = (
  slotIndex: number,
  progression: readonly Chord[],
  key: Scale
): Substitution => {
  const current = progression[slotIndex]
  if (!isChordVariant(current)) {
    return unavailable('borrowed-flat-VII', current, 'role-mismatch')
  }
  if (getDiatonicFunction(current, key) !== 'V') {
    return unavailable('borrowed-flat-VII', current, 'role-mismatch')
  }
  // ♭VII = the diatonic 7th-scale-degree note lowered by a semitone, with
  // major triad on top. Using ScaleLib for spelling means C-major's ♭VII is
  // `Bb` (not `A#`) and G-major's is `F` (not `E#`).
  const seventhDegreeNote = ScaleLib.getNotes(key)[6]
  if (!seventhDegreeNote) {
    return unavailable('borrowed-flat-VII', current, 'role-mismatch')
  }
  const subRoot = lowerNoteKeepingLetter(seventhDegreeNote)
  const synthesized: Chord = { root: subRoot, variant: '' }
  return {
    technique: 'borrowed-flat-VII',
    kind: 'replace',
    chord: synthesized,
    lexiconId: LEXICON_IDS['borrowed-flat-VII'],
    available: true,
    romanLabel: '♭VII',
  }
}

const buildDeceptiveResolution = (
  slotIndex: number,
  progression: readonly Chord[],
  key: Scale
): Substitution => {
  const current = progression[slotIndex]
  const next = progression[slotIndex + 1]

  // Bowie §8.5 — strict: disabled when slot has no next chord (last-slot
  // case). The deceptive substitute replaces the *next* chord; without a next
  // there's nothing to redirect.
  if (!next) {
    return unavailable('deceptive-resolution', current, 'slot-position')
  }
  if (!isChordVariant(current)) {
    return unavailable('deceptive-resolution', current, 'role-mismatch')
  }
  if (getDiatonicFunction(current, key) !== 'V') {
    return unavailable('deceptive-resolution', current, 'role-mismatch')
  }
  if (!isChordVariant(next) || getDiatonicFunction(next, key) !== 'I') {
    return unavailable('deceptive-resolution', next, 'next-chord-mismatch')
  }

  // Synthesize vi: diatonic vi triad of the key.
  const vi = ScaleLib.getChordByScaleDegree(key, 'VI')
  if (!vi || !isChordVariant(vi)) {
    return unavailable('deceptive-resolution', next, 'role-mismatch')
  }

  return {
    technique: 'deceptive-resolution',
    kind: 'replace-next',
    chord: vi,
    lexiconId: LEXICON_IDS['deceptive-resolution'],
    available: true,
    romanLabel: 'vi',
  }
}
